import { LocalDateTime, LocalTime } from 'js-joda';
import {
  SeatingTemplateTable,
  TEMPLATE_INFO_BUSINESS_DAY,
  getConditionalRangeAtTimeBeforeServiceForTicketType,
  getConditionalRangeTemplateForSTTTicketTypeConfig,
} from 'state/templateInfo';
import { StoreType } from '@tock/shared/utils/connect';
import { TicketTypeInfoMap, TicketTypeSlimInfo } from 'types/tableBasedTemplates';
import {
  adjustRangeForBusinessDay,
  fastParseTime,
  isDateTimeInRange,
  isTimeInBusinessDayRange,
} from 'utils/timeUtils';
import { computeTableOptimizationsForSeatingTemplateTables } from 'utils/inventory/dynamicOptimizationUtils';
import { core } from '@tock/coretypes';
import { createSelector } from 'reselect';
import { dashboard } from 'types';
import {
  getFloorplanTableCombinationsFromTemplate,
  selectedDynamicOptimizationTimeSelector,
} from 'selectors/templateInfoSelector';
import { getTicketTypeIdFromSeatingTemplateTable } from 'state/hooks/inventoryScheduleUtils';
import DayTimeRange from 'utils/DayTimeRange';
import _ from 'lodash';
import createCachedSelector from 're-reselect';
import ticketTypeIdToSlimInfoSelector from 'selectors/routes/table-based-templates/ticketTypeIdToSlimInfoSelector';

interface SeatingTemplateTableFilters {
  tableId: number;
  serviceType?: core.Service.Type;
}

interface SeatingTemplateTableCombinationFilters {
  tableCombinationId: number;
  serviceType?: core.Service.Type;
}

export interface SeatingTemplateTicketInfo {
  ticketInfo: TicketTypeSlimInfo;
}

/**
 * Returns an active seating template table for the matching tableId and optional service type
 */
export const filterSeatingTemplateTables = createCachedSelector(
  (state: StoreType) => state.templateInfo.templateTables,
  (_state: StoreType, filters: SeatingTemplateTableFilters) => filters,
  (templateTables, { tableId }) => {
    return _.find(templateTables, (templateTable) => templateTable.tableId === tableId);
  }
)((state: StoreType, { tableId, serviceType }: SeatingTemplateTableFilters) =>
  serviceType ? `${tableId}:${serviceType}` : tableId
);

/**
 * Returns an active seating template table combo for the matching tableId and optional service type
 */
export const filterSeatingTemplateTablesForCombo = createCachedSelector(
  (state: StoreType) => state.templateInfo.templateTables,
  getFloorplanTableCombinationsFromTemplate,
  (_state: StoreType, filters: SeatingTemplateTableCombinationFilters) => filters,
  (templateTables, floorplanTableCombinations, { tableCombinationId }) => {
    // Get table combo
    const floorplanTableCombination = floorplanTableCombinations.find(
      (floorplanTableCombo) => floorplanTableCombo.id === tableCombinationId
    );
    const floorplanTableIdsInCombo = floorplanTableCombination?.tableId ?? [];

    return templateTables.filter((templateTable) =>
      floorplanTableIdsInCombo.includes(templateTable.tableId)
    );
  }
)(
  (
    state: StoreType,
    { tableCombinationId, serviceType }: SeatingTemplateTableCombinationFilters
  ) => (serviceType ? `${tableCombinationId}:${serviceType}` : tableCombinationId)
);

/**
 * Returns an active seating template table combo for the matching tableId and optional service type
 */
export const filterSeatingTemplateTableCombinations = createCachedSelector(
  (state: StoreType) => state.templateInfo.templateTableCombinations,
  (_state: StoreType, filters: SeatingTemplateTableCombinationFilters) => filters,
  (templateTableCombinations, { tableCombinationId }) => {
    return templateTableCombinations.find(
      (templateTableCombination) =>
        templateTableCombination.tableCombinationId === tableCombinationId
    );
  }
)(
  (
    _state: StoreType,
    { tableCombinationId, serviceType }: SeatingTemplateTableCombinationFilters
  ) => (serviceType ? `${tableCombinationId}:${serviceType}` : tableCombinationId)
);

/**
 * Returns currently selected tickets and service type for all templates within a table.
 */
export const getTicketInfoForTable = createCachedSelector(
  ticketTypeIdToSlimInfoSelector,
  (state: StoreType) => state.templateInfo.template,
  (state: StoreType) => state.templateInfo.templateTables,
  (_state: StoreType, tableId: number) => tableId,
  (ticketsById, template, seatingTemplates, tableId): SeatingTemplateTicketInfo[] => {
    const filteredTemplates = _.filter(seatingTemplates, { tableId });

    /* Single service blueprints exisit that have multiple template tables for a single
       table, this is a failsafe to assure we only grab one ticket info for those blueprints */
    filteredTemplates.splice(1);

    return _.map(filteredTemplates, (template) => {
      const ticketTypeId = getTicketTypeIdFromSeatingTemplateTable(template) ?? 0;
      return {
        ticketInfo: ticketsById[ticketTypeId],
      };
    });
  }
)((state: StoreType, tableId: number) => tableId);

/**
 * Returns ticket info using the ticketTypeId in a SeatingTemplateTable
 */
export const getTicketFromSeatingTemplateTable = createCachedSelector(
  ticketTypeIdToSlimInfoSelector,
  filterSeatingTemplateTables,
  (ticketsById, seatingTemplate) => {
    const ticketTypeIds = seatingTemplate?.ticketTypeId;
    return ticketTypeIds?.map((ticketTypeId) => ticketsById[ticketTypeId]);
  }
)((_state: StoreType, { tableId, serviceType }: SeatingTemplateTableFilters) =>
  serviceType ? `${tableId}:${serviceType}` : tableId
);

export function getServiceTimeRangeForScheduledTemplate(
  template: dashboard.SeatingTemplate
): DayTimeRange {
  const serviceTimeRange = DayTimeRange.create();
  if (template.startTime && template.endTime) {
    serviceTimeRange.union(template.startTime, template.endTime);
  }
  return serviceTimeRange;
}

export const getTicketTypeIdsByTableAndTime = createSelector(
  (state: StoreType) => state.templateInfo.templateTables,
  (state: StoreType) => state.templateInfo.template,
  selectedDynamicOptimizationTimeSelector,
  (seatingTemplateTables, seatingTemplate, selectedDynamicOptimizationTimeMinutes) => {
    const serviceTimeRange = getServiceTimeRangeForScheduledTemplate(seatingTemplate);
    return getTicketTypesByTableElementAndTime(
      seatingTemplateTables,
      serviceTimeRange,
      selectedDynamicOptimizationTimeMinutes
    );
  }
);

export function getTicketTypesByTableElementAndTime(
  tableElement: SeatingTemplateTable[] | dashboard.SeatingTemplateTableCombination[],
  serviceTimeRange: DayTimeRange,
  selectedDynamicOptimizationTimeMinutes: number | null
) {
  const ticketTypesByTableAndTime: Map<number, Map<string, number[]>> = new Map();
  tableElement.forEach((tableElement) => {
    const experiencesByTime: Map<string, number[]> = new Map();

    const ticketTypeConfigs = tableElement?.ticketTypeConfig ?? [];

    const increments = serviceTimeRange.getIncrementsAsLocalTimes();

    increments.forEach((increment) => {
      const ticketTypeIdsAtTime = getTicketTypeIdsAtTime(
        ticketTypeConfigs,
        increment,
        selectedDynamicOptimizationTimeMinutes
      );
      experiencesByTime.set(increment.toString(), ticketTypeIdsAtTime);
    });

    const tableElementId =
      'tableCombinationId' in tableElement ? tableElement.tableCombinationId : tableElement.tableId;

    ticketTypesByTableAndTime.set(tableElementId, experiencesByTime);
  });

  return ticketTypesByTableAndTime;
}

export const getTicketInfosFromSeatingTemplateTableAtTime = createCachedSelector(
  ticketTypeIdToSlimInfoSelector,
  selectedDynamicOptimizationTimeSelector,
  filterSeatingTemplateTables,
  (_state: StoreType, _filters: SeatingTemplateTableFilters, dateTime: LocalDateTime) => dateTime,
  (ticketsById, selectedDynamicOptimizationTimeMinutes, seatingTemplateTable, dateTime) => {
    if (!seatingTemplateTable) {
      return [];
    }
    const ticketTypeIdsAtTime = getEffectiveActiveExperiencesAtTime(
      seatingTemplateTable,
      dateTime,
      selectedDynamicOptimizationTimeMinutes
    );
    return ticketTypeIdsAtTime?.map((ticketTypeId) => ticketsById[ticketTypeId]);
  }
)((_state: StoreType, { tableId, serviceType }: SeatingTemplateTableFilters, dateTime) =>
  serviceType ? `${tableId}:${serviceType}:${dateTime}` : `${tableId}:${dateTime}`
);

interface TicketTypesBySeatingTemplateTable {
  [tableId: number]: number[];
}

export const getTicketInfosFromSeatingTemplateTableCombinationAtTime = createCachedSelector(
  ticketTypeIdToSlimInfoSelector,
  selectedDynamicOptimizationTimeSelector,
  filterSeatingTemplateTablesForCombo,
  (_state: StoreType, _filters: SeatingTemplateTableCombinationFilters, dateTime: LocalDateTime) =>
    dateTime,
  filterSeatingTemplateTableCombinations,
  getActiveExperiencesForSeatingTemplateCombinationAtTime
)(
  (
    _state: StoreType,
    { tableCombinationId, serviceType }: SeatingTemplateTableCombinationFilters,
    dateTime
  ) =>
    serviceType
      ? `${tableCombinationId}:${serviceType}:${dateTime}`
      : `${tableCombinationId}:${dateTime}`
);

// exported for testing
export function getActiveExperiencesForSeatingTemplateCombinationAtTime(
  ticketsById: TicketTypeInfoMap,
  selectedDynamicOptimizationTimeMinutes: number | null,
  seatingTemplateTables: SeatingTemplateTable[],
  dateTime: LocalDateTime,
  seatingTemplateTableCombination: dashboard.SeatingTemplateTableCombination | undefined
) {
  const ticketTypesBySeatingTemplateTable: TicketTypesBySeatingTemplateTable = {};

  const seatingTemplateTableComboHasOwnAvailability =
    seatingTemplateTableCombination?.ticketTypeConfig?.length ?? 0 > 0;

  // Short-circuit if combo has its own availability
  if (seatingTemplateTableComboHasOwnAvailability) {
    const ticketTypeConfigs = seatingTemplateTableCombination?.ticketTypeConfig ?? [];
    const ticketTypeIdsAtTime = getTicketTypeIdsAtTime(
      ticketTypeConfigs,
      dateTime.toLocalTime(),
      selectedDynamicOptimizationTimeMinutes
    );
    return ticketTypeIdsAtTime?.map((ticketTypeId) => ticketsById[ticketTypeId]);
  }

  seatingTemplateTables.forEach((seatingTemplateTable) => {
    const ticketTypeIdsAtTime = getEffectiveActiveExperiencesAtTime(
      seatingTemplateTable,
      dateTime,
      selectedDynamicOptimizationTimeMinutes
    );
    ticketTypesBySeatingTemplateTable[seatingTemplateTable.id] = ticketTypeIdsAtTime;
  });

  const commonExperienceIds = getExperiencesIntersectionForTablesInCombo(
    ticketTypesBySeatingTemplateTable
  );
  return commonExperienceIds?.map((ticketTypeId) => ticketsById[ticketTypeId]);
}

function getEffectiveActiveExperiencesAtTime(
  seatingTemplateTable: SeatingTemplateTable,
  dateTime: LocalDateTime,
  selectedDynamicOptimizationTimeMinutes: number | null
) {
  const ticketTypeIds = seatingTemplateTable?.ticketTypeId ?? [];
  const ticketTypeConfigs = seatingTemplateTable?.ticketTypeConfig ?? [];

  const ticketTypeIdsWithConfigs = ticketTypeConfigs.map((config) => config.ticketTypeId);

  // These ticket types do not have corresponding configs and are treated as full-service experience applications
  const ticketTypeIdsWithoutConfig = ticketTypeIds.filter(
    (ticketTypeId) => !ticketTypeIdsWithConfigs.includes(ticketTypeId)
  );

  const ticketTypeIdsAtTime = getTicketTypeIdsAtTime(
    ticketTypeConfigs,
    dateTime.toLocalTime(),
    selectedDynamicOptimizationTimeMinutes
  );
  const effectiveTicketTypeIdsAtTime = [...ticketTypeIdsAtTime, ...ticketTypeIdsWithoutConfig];

  return effectiveTicketTypeIdsAtTime;
}

function getTicketTypeIdsAtTime(
  ticketTypeConfigs: dashboard.ISeatingTemplateTableTicketTypeConfig[],
  time: LocalTime,
  selectedDynamicOptimizationTimeMinutes: number | null
) {
  const ticketTypeIdsAtTime: number[] = [];

  ticketTypeConfigs.forEach((config) => {
    // If we have a selected dynamic optimization time frame to show, we get the range relevant to that time.
    // Otherwise we display the "template" range which will include all experiences / time ranges superimposed upon one another.
    const effectiveConditionalRange = selectedDynamicOptimizationTimeMinutes
      ? getConditionalRangeAtTimeBeforeServiceForTicketType(
          config,
          selectedDynamicOptimizationTimeMinutes
        )
      : getConditionalRangeTemplateForSTTTicketTypeConfig(config).templateRange;

    // No conditional range or conditional range with null bookableRange indicates full-service application
    if (!effectiveConditionalRange || !effectiveConditionalRange.bookableRanges?.bookableRange) {
      ticketTypeIdsAtTime.push(Number(config.ticketTypeId));
    }

    effectiveConditionalRange?.bookableRanges?.bookableRange?.forEach((range) => {
      if (
        range.startTimeInclusive &&
        range.endTimeExclusive &&
        isTimeInBusinessDayRange(
          time,
          fastParseTime(range.startTimeInclusive),
          fastParseTime(range.endTimeExclusive)
        )
      ) {
        ticketTypeIdsAtTime.push(Number(config.ticketTypeId));
      }
    });
  });

  return ticketTypeIdsAtTime;
}

function getExperiencesIntersectionForTablesInCombo(
  ticketTypesBySeatingTemplateTable: TicketTypesBySeatingTemplateTable
) {
  const tableIds = Object.keys(ticketTypesBySeatingTemplateTable);

  if (tableIds.length === 0) {
    return [];
  }

  const tableIdsAsNumbers = tableIds.map((id) => Number(id));

  let commonExperiences = ticketTypesBySeatingTemplateTable[tableIdsAsNumbers[0]];

  for (let i = 1; i < tableIdsAsNumbers.length; i++) {
    commonExperiences = commonExperiences.filter((num) =>
      ticketTypesBySeatingTemplateTable[tableIdsAsNumbers[i]].includes(num)
    );
  }

  return commonExperiences;
}

/**
 * Returns true if the supplied time has a hold on the SeatingTemplateTable
 */
export const getIsBlocked = createCachedSelector(
  filterSeatingTemplateTables,
  (_state: StoreType, _filters: SeatingTemplateTableFilters, dateTime: LocalDateTime) => dateTime,
  (seatingTemplateTable, dateTime): boolean => {
    return seatingTemplateTable
      ? _.some(seatingTemplateTable.block, (block) => {
          if (!block.startTime || !block.endTime) {
            return false;
          }
          const { start, end } = adjustRangeForBusinessDay(
            TEMPLATE_INFO_BUSINESS_DAY,
            block.startTime,
            block.endTime
          );
          return isDateTimeInRange(dateTime, start, end);
        })
      : false;
  }
)((_state: StoreType, { tableId, serviceType }: SeatingTemplateTableFilters, dateTime) =>
  serviceType ? `${tableId}:${serviceType}:${dateTime}` : `${tableId}:${dateTime}`
);

/**
 * Returns true if the supplied time has a block on any of the SeatingTemplateTables that make up the combination
 */
export const getIsTableCombinationBlocked = createCachedSelector(
  filterSeatingTemplateTablesForCombo,
  (_state: StoreType, _filters: SeatingTemplateTableCombinationFilters, dateTime: LocalDateTime) =>
    dateTime,
  getIsTimeBlockedOnAnyTable
)(
  (
    _state: StoreType,
    { tableCombinationId, serviceType }: SeatingTemplateTableCombinationFilters,
    dateTime
  ) =>
    serviceType
      ? `${tableCombinationId}:${serviceType}:${dateTime}`
      : `${tableCombinationId}:${dateTime}`
);

// exported for testing
export function getIsTimeBlockedOnAnyTable(
  seatingTemplateTables: SeatingTemplateTable[],
  dateTime: LocalDateTime
): boolean {
  const anyTableInComboHasBlock = seatingTemplateTables?.find((seatingTemplateTable) => {
    return seatingTemplateTable.block?.find((block) => {
      if (!block.startTime || !block.endTime) {
        return false;
      }
      const { start, end } = adjustRangeForBusinessDay(
        TEMPLATE_INFO_BUSINESS_DAY,
        block.startTime,
        block.endTime
      );
      return isDateTimeInRange(dateTime, start, end);
    });
  });

  return !!anyTableInComboHasBlock;
}

/**
 * Returns true if the supplied time has a hold on the SeatingTemplateTable
 */
export const getIsHeld = createCachedSelector(
  filterSeatingTemplateTables,
  (_state: StoreType, _filters: SeatingTemplateTableFilters, dateTime: LocalDateTime) => dateTime,
  (seatingTemplate, dateTime): boolean => {
    return seatingTemplate
      ? _.some(seatingTemplate.hold, (hold) => {
          if (!hold.startTime || !hold.finishedByTime) {
            return false;
          }
          const { start, end } = adjustRangeForBusinessDay(
            TEMPLATE_INFO_BUSINESS_DAY,
            hold.startTime,
            hold.finishedByTime
          );
          return isDateTimeInRange(dateTime, start, end);
        })
      : false;
  }
)((_state: StoreType, { tableId, serviceType }: SeatingTemplateTableFilters, dateTime) =>
  serviceType ? `${tableId}:${serviceType}:${dateTime}` : `${tableId}:${dateTime}`
);

/**
 * Returns a summary view regarding which tables have "add" / "remove" optimizations by experience and time. When dynamic
 * optimization has more complex requirements (i.e., multiple time cutoffs), we'll want to rethink this based on display
 * needs in the UI.
 */
export const getTableOptimizationsByTimeAndExperience = createSelector(
  (state: StoreType) => state.templateInfo.templateTables,
  (seatingTemplateTables) =>
    computeTableOptimizationsForSeatingTemplateTables(seatingTemplateTables)
);
